import { ReadonlyURLSearchParams } from "next/navigation";
import { logClientError } from "@repo/next-utils/newrelic-client-utils";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";
import { getSierraConfig } from "./get-sierra-config";
import { getSierraInstance } from "./get-sierra-instance";

export const setChatWidgetParam = () => {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.set("showChatWidget", "true");
    window.history.replaceState({}, "", url.toString());
  }
};

export const deleteChatWidgetParam = () => {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.delete("showChatWidget");
    window.history.replaceState({}, "", url.toString());
  }
};

type SierraChatTimeoutActionParams = {
  searchParams: ReadonlyURLSearchParams | null;
  setInitError: (error: Error | null) => void;
  setShowSierraChat: (show: boolean) => void;
};

export const sierraChatTimeoutAction = ({
  searchParams,
  setInitError,
  setShowSierraChat,
}: SierraChatTimeoutActionParams) => {
  const timeout = setTimeout(() => {
    const sierraChatContainer = document.querySelector(
      "#sierra-chat-container",
    );

    const sierraChatWidget =
      sierraChatContainer?.shadowRoot?.querySelector("main");

    if (!sierraChatWidget && searchParams?.has("showChatWidget")) {
      deleteChatWidgetParam();
      const timeoutError = new Error("Sierra Chat: Timeout error");
      logClientError({ error: timeoutError });
      setShowSierraChat(false);
      setInitError(timeoutError);
    }
  }, 5000);

  return timeout;
};

type InitParams = {
  auth: ReturnType<typeof useAuth>;
  searchParams: ReadonlyURLSearchParams | null;
  store: ReturnType<typeof useStore>;
  setShowSierraChat: (show: boolean) => void;
  toggleChatModal: () => void;
};

export const init = async ({
  auth,
  searchParams,
  store,
  setShowSierraChat,
  toggleChatModal,
}: InitParams) => {
  const sierraInstance = getSierraInstance({ searchParams });

  window.sierraConfig = await getSierraConfig({
    auth,
    searchParams,
    store,
  });

  const agentScript = document.createElement("script");
  agentScript.type = "module";
  agentScript.onload = () => {
    if (searchParams?.get("showChatWidget") === "true") {
      toggleChatModal();
    }
  };
  agentScript.onerror = () => {
    const error = new Error("Sierra Chat: Script load error.");
    logClientError({ error });
    setShowSierraChat(false);
  };
  agentScript.src = sierraInstance;
  document.head.appendChild(agentScript);

  return () => {
    document.head.removeChild(agentScript);
  };
};
